import { forEach } from 'lodash';
import { ReportViewModel, Column, ReportRow } from '../types';
import { sortAuditIssues, getMostProbableTransactions } from './sorter';
import {
  DataTransaction,
  KSAccount,
  KSAuditedTransaction,
  KSTransaction,
  KSTransactionsProps,
  KSVendor,
  KSVendorAccount,
} from './types';
import { convertDataTransactionToKSTransaction, calculateStats } from './utils';
import { evalOutliers } from './outliers';
import { detectTemporalAnomalies, PeriodicityType } from './anomalies';
import { detectVendorLifecycleAnomalies, detectVendorPeriods } from './vendorLifetime';
import { prepareForLLMAnalysis, prepareLLMPrompt } from './llmPrep';

/**
 * Processes transformed data to extract transactions, accounts, vendors, and audit information
 * @param transformedData The transformed data to process
 * @returns Object containing transactions, accounts, vendors, and auditedTransactions
 */
export const getTransactions = (
  transformedData: ReportViewModel
): {
  transactions: KSTransaction[];
  accounts: KSAccount[];
  vendors: KSVendor[];
  auditedTransactions: KSAuditedTransaction;
} => {
  const transactions: KSTransaction[] = [];
  const accounts: KSAccount[] = [];
  const vendors: KSVendor[] = [];
  const auditedTransactions: KSAuditedTransaction = {
    outlierIds: [],
    outliers: [],
    anomalyIds: [],
    anomalies: [],
    flaggedTransactions: [],
    flaggedIds: [],
    periodicityPatterns: {},
  };
  const columns = transformedData.columns;

  // iterate rows
  forEach(columns[0].data, (row: ReportRow, rowIndex: number) => {
    const account: KSAccount = {
      id: rowIndex,
      glCode: row.glCode,
      accountId: row.id,
      name: row.amountTitle,
      stats: null,
      auditedTransactions: null,
    };

    // iterate columns
    forEach(columns, (column: Column, index: number) => {
      if (index > 0 && column.data && column.data[rowIndex].dataRows.length > 0) {
        // iterate data rows
        forEach(column.data[rowIndex].dataRows, (dataTransaction: DataTransaction) => {
          // transform transactions into KSTransaction
          const transaction: KSTransaction = convertDataTransactionToKSTransaction(
            dataTransaction,
            index - 1,
            row.id,
            transactions.length
          );

          // Extract vendor information from the transaction
          const existingVendor = vendors.find(vendor => vendor.name === transaction.name);
          if (existingVendor) {
            if (!existingVendor.glAccounts.some(acc => acc.accountId === account.id)) {
              existingVendor.glAccounts.push({
                accountId: account.id,
                stats: null,
              });
            }
            transaction.vendorId = existingVendor.id;
          } else {
            vendors.push({
              id: vendors.length,
              name: transaction.name,
              categories: [],
              externalId: '',
              glAccounts: [
                {
                  accountId: account.id,
                  stats: null,
                },
              ],
            });
            transaction.vendorId = vendors.length - 1;
          }
          transactions.push(transaction);
        });
      }
    });
    accounts.push(account);
  });

  // second pass to add stats to vendors and detect outliers
  forEach(vendors, (vendor: KSVendor) => {
    forEach(vendor.glAccounts, (glAccount: KSVendorAccount) => {
      const account = accounts.find(acc => acc.id === glAccount.accountId);
      if (account) {
        const trxs = transactions.filter(trx => trx.vendorId === vendor.id && trx.accountId === account.accountId);
        if (trxs.length > 0) {
          const stats = calculateStats(trxs.map(trx => trx.amount));
          // second pass to add auditedTransactions
          const outliers: { ids: number[]; transactions: KSTransaction[] } = evalOutliers(transactions, trxs, stats);
          if (trxs.length > 3) {
            // Detect temporal patterns and anomalies
            const temporalAnomalies = detectTemporalAnomalies(trxs);
            const vendorPeriods = detectVendorPeriods(trxs);
            const lifecycleAnomalies = detectVendorLifecycleAnomalies(trxs, vendorPeriods);

            // Extract periodicity information
            let periodicity: PeriodicityType = 'unknown';
            const periodicityConfidence = 0;

            if (temporalAnomalies.length > 0 && temporalAnomalies[0].periodicity) {
              periodicity = temporalAnomalies[0].periodicity;

              // Store periodicity information for this vendor and account
              auditedTransactions.periodicityPatterns[`${vendor.id}-${account.id}`] = {
                periodicity,
                confidence: temporalAnomalies[0].confidence || 0.5,
                vendorName: vendor.name,
                accountName: account.name,
              };
            }

            // Add anomalies to the account's auditedTransactions
            const anomalyIds = temporalAnomalies.map(a => a.transaction.id);

            // Initialize account.auditedTransactions if it doesn't exist
            if (!account.auditedTransactions) {
              account.auditedTransactions = {
                outlierIds: [],
                outliers: [],
                anomalyIds: [],
                anomalies: [],
                flaggedTransactions: [],
                flaggedIds: [],
                periodicityPatterns: {},
              };
            }

            account.auditedTransactions.anomalyIds = anomalyIds;
            account.auditedTransactions.anomalies = temporalAnomalies.map(a => a.transaction);

            // Add anomalies to the global auditedTransactions
            auditedTransactions.anomalyIds = auditedTransactions.anomalyIds.concat(anomalyIds);
            auditedTransactions.anomalies = auditedTransactions.anomalies.concat(
              temporalAnomalies.map(a => a.transaction)
            );
          }
          account.stats = stats;
          // Only initialize if it doesn't already exist
          if (!account.auditedTransactions) {
            account.auditedTransactions = {
              outlierIds: outliers.ids,
              outliers: outliers.transactions,
              anomalyIds: [],
              anomalies: [],
              flaggedTransactions: [],
              flaggedIds: [],
              periodicityPatterns: {},
            };
          } else {
            // Update with outlier information
            account.auditedTransactions.outlierIds = outliers.ids;
            account.auditedTransactions.outliers = outliers.transactions;
          }
          auditedTransactions.outlierIds = auditedTransactions.outlierIds.concat(outliers.ids);
          auditedTransactions.outliers = auditedTransactions.outliers.concat(outliers.transactions);
        }
      }
    });
  });
  return { transactions, accounts, vendors, auditedTransactions };
};

/**
 * Compiles transactions data with enhanced analysis and auditing
 * @param transformedData The transformed data to process
 * @returns KSTransactionsProps object with enhanced audit information
 */
export const compileTransactions = (transformedData: ReportViewModel): KSTransactionsProps => {
  //console.time('compileTransactions');
  const { transactions, accounts, vendors, auditedTransactions } = getTransactions(transformedData);
  const trxs: KSTransactionsProps = {
    transactions: transactions,
    vendors: vendors,
    accounts: accounts,
    auditedTransactions: auditedTransactions,
  };
  const auditResults = sortAuditIssues(auditedTransactions);

  // Get the most probable transactions based on priority and confidence
  const mostProbableIssues = getMostProbableTransactions(auditResults, {
    limit: 10, // Limit to 10 transactions to avoid overwhelming accountants
    minPriority: 'medium',
    minConfidenceTier: 'high',
    prioritizeAmount: true,
  });

  // Add most probable transactions to audited transactions for easy access
  auditedTransactions.mostProbableTransactions = {
    transactions: mostProbableIssues.transactions,
    ids: mostProbableIssues.ids,
  };

  // Prepare data for LLM analysis
  const enhancedAuditedTransactions = prepareForLLMAnalysis(auditedTransactions, accounts, vendors);
  trxs.auditedTransactions = enhancedAuditedTransactions;

  // Generate LLM prompt with transaction data for RAG-like approach
  // Commented out as per your request to not include llmPrompt generation
  // const llmPrompt = prepareLLMPrompt(enhancedAuditedTransactions, vendors, accounts);

  // Logging disabled for production
  // Uncomment for debugging:

  /* console.log(trxs);
  console.log('Audit Results:', auditResults.sortedTransactions.length, auditResults);
  
  console.log('Audit Results:', auditResults.sortedTransactions.length, auditResults);
  console.log('Most Probable Transactions:', mostProbableIssues.transactions.length);

  // Log periodicity patterns if they exist
  if (Object.keys(auditedTransactions.periodicityPatterns).length > 0) {
    console.log('Periodicity Patterns:', auditedTransactions.periodicityPatterns);
  }
  
  // Log prioritized anomalies
  if (enhancedAuditedTransactions.prioritizedAnomalies) {
    const criticalCount = enhancedAuditedTransactions.prioritizedAnomalies.critical.length;
    const highCount = enhancedAuditedTransactions.prioritizedAnomalies.high.length;
    const mediumCount = enhancedAuditedTransactions.prioritizedAnomalies.medium.length;
    
    console.log(`Prioritized Anomalies: ${criticalCount} critical, ${highCount} high, ${mediumCount} medium`);
    
    if (criticalCount > 0) {
      console.log('Top Critical Anomalies:', enhancedAuditedTransactions.prioritizedAnomalies.critical.slice(0, 3));
    }
  }
  
  // Log LLM prompt for debugging
  // console.log('LLM Prompt:\n', llmPrompt);
  */

  //console.timeEnd('compileTransactions');
  return trxs;
};
